import { CaptionsRenderer } from '@sparemin/text-effects';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  createLayoutSegmentByIdSelector,
  isCaptionsEnabledSelector,
} from 'redux/modules/embed/selectors';
import { millisToSec, secToMillis } from 'utils/time';
import { useEditSceneModalPlaybackContext } from '../EditSceneModalPlaybackContext';
import useEditSceneModal from '../useEditSceneModal';

type UseEditSceneCaptionsTimeSyncProps = {
  captionsRendererRef: React.MutableRefObject<CaptionsRenderer | undefined>;
};

export default function useEditSceneCaptionsTimeSync({
  captionsRendererRef,
}: UseEditSceneCaptionsTimeSyncProps) {
  const { playerRef } = useEditSceneModalPlaybackContext();
  const { videoId, segmentId } = useEditSceneModal();
  const isCaptionsEnabled = useSelector(isCaptionsEnabledSelector);

  const layoutSegment = useSelector(
    createLayoutSegmentByIdSelector(videoId, segmentId),
  );
  const playbackStartSec = millisToSec(layoutSegment?.playOffsetMillis ?? 0);

  // sync playback state
  useEffect(() => {
    if (!isCaptionsEnabled) {
      return undefined;
    }

    const player = playerRef.current;
    if (!player) {
      return undefined;
    }

    const updatePlaying = () => {
      const animator = captionsRendererRef.current?.getAnimator();
      if (!animator) {
        return;
      }

      const isPlaying = !player.paused;
      if (isPlaying && animator.paused) {
        animator.play();
      } else if (!isPlaying && !animator.paused) {
        animator.pause();
      }
    };

    // Check immediately
    updatePlaying();

    player.addEventListener('play', updatePlaying);
    player.addEventListener('pause', updatePlaying);

    return () => {
      player.removeEventListener('play', updatePlaying);
      player.removeEventListener('pause', updatePlaying);
    };
  }, [captionsRendererRef, isCaptionsEnabled, playerRef]);

  // sync playback time
  useEffect(() => {
    if (!isCaptionsEnabled) {
      return undefined;
    }

    const player = playerRef.current;
    if (!player) {
      return undefined;
    }

    const updateTime = () => {
      const animator = captionsRendererRef.current?.getAnimator();
      if (!animator) {
        return;
      }

      // get segment-relative time
      const segmentRelativeTime = player.currentTime - playbackStartSec;
      const timeMillis = secToMillis(segmentRelativeTime);

      // if animationm is playing, seeking does not work properly in anime.js.
      // pause and play again after seek to ensure it takes effect (same pattern as
      // embed-service)
      const wasPlaying = !animator.paused;
      if (wasPlaying) {
        animator.pause();
      }

      animator.seek(timeMillis);

      if (wasPlaying) {
        animator.play();
      }
    };

    // listen to seeked events (immediate sync after seeks).  the animator is allowed
    // to run independently from video playback.  if more syncing is needed, we can
    // listen for timeupdate events and periodically sync the animator to the video time
    player.addEventListener('seeked', updateTime);

    // initial sync
    updateTime();

    return () => {
      player.removeEventListener('seeked', updateTime);
    };
  }, [captionsRendererRef, isCaptionsEnabled, playbackStartSec, playerRef]);
}
